import { faFileArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { SUPPORTED_ORDER_IMPORT_EXTENSIONS } from '../../../client-queries/order-import/types';

type OrderImportFileUploadProps = {
  title: string;
  supportedText: string;
  onFileSelected?: (file: File) => void;
  maxSizeMb?: number;
  'data-testid'?: string;
};

const DEFAULT_MAX_SIZE_MB = 5;

// Convert Set to array of file extensions with dot prefix for HTML accept attribute
const getSupportedExtensionsForAccept = () => {
  return Array.from(SUPPORTED_ORDER_IMPORT_EXTENSIONS).map(ext => `.${ext}`);
};

const UploadShellStyles = cva(
  'rounded-orderImport-upload-section border-2 border-dashed border-orderImport-upload-section-border bg-orderImport-upload-section-bg p-10 text-center',
);

const HiddenInputStyles = cva('hidden');
const UploadIconWrapperStyles = cva(
  'mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-accentBg text-green-dark',
);
const UploadTitleStyles = cva('mb-2 text-xl font-bold text-green-dark');
const UploadSupportedTextStyles = cva('text-sm text-gray');
const UploadFileNameStyles = cva('mt-4 text-sm font-semibold text-green-dark');
const UploadErrorStyles = cva('mt-4 text-sm text-red');

const OrderImportFileUpload = ({
  title,
  supportedText,
  onFileSelected,
  maxSizeMb = DEFAULT_MAX_SIZE_MB,
  'data-testid': dataTestId,
}: OrderImportFileUploadProps) => {
  const { t } = useTranslation('account');
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxBytes = maxSizeMb * 1024 * 1024;
  const acceptedExtensions = getSupportedExtensionsForAccept();

  const isExtensionSupported = (fileName: string) => {
    const lowerCaseName = fileName.toLowerCase();

    return Array.from(SUPPORTED_ORDER_IMPORT_EXTENSIONS).some(extension => lowerCaseName.endsWith(`.${extension}`));
  };

  const validateFile = (file: File) => {
    if (!isExtensionSupported(file.name)) {
      setError(String(t('orderImport.errors.file.unsupportedType')));
      return false;
    }

    if (file.size > maxBytes) {
      setError(String(t('orderImport.errors.file.exceededSize', { maxSizeMb })));
      return false;
    }

    setError('');
    return true;
  };

  const processSelectedFile = (file: File | null) => {
    if (!file) {
      return;
    }

    if (!validateFile(file)) {
      return;
    }

    setSelectedFileName(file.name);
    onFileSelected?.(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0] ?? null;

    processSelectedFile(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    processSelectedFile(file);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    fileInputRef.current?.click();
  };

  const errorId = 'file-upload-error';
  const supportTextId = 'file-upload-help';

  return (
    <div
      aria-describedby={supportTextId}
      aria-label={String(t('orderImport.upload.dropzoneLabel'))}
      className={twMerge(UploadShellStyles(), isDragActive && 'opacity-90')}
      data-testid={dataTestId ?? 'order-import-file-upload'}
      onClick={() => fileInputRef.current?.click()}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <input
        accept={acceptedExtensions.join(',')}
        aria-errormessage={error ? errorId : undefined}
        aria-invalid={!!error}
        aria-label={String(t('orderImport.upload.fileInputLabel'))}
        className={HiddenInputStyles()}
        onChange={handleInputChange}
        ref={fileInputRef}
        type="file"
      />

      <div className={UploadIconWrapperStyles()}>
        <FontAwesomeIcon icon={faFileArrowUp} size="lg" />
      </div>

      <p className={UploadTitleStyles()}>{title}</p>
      <p className={UploadSupportedTextStyles()} id={supportTextId}>
        {supportedText}
      </p>

      {!!selectedFileName && <p className={UploadFileNameStyles()}>{selectedFileName}</p>}
      {!!error && (
        <p aria-live="polite" className={UploadErrorStyles()} id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default OrderImportFileUpload;
